import { REQUESTED_START_LABEL } from '@ruta/shared';

/**
 * Safely parse date strings, handling both date-only (YYYY-MM-DD) and full ISO formats
 * All date strings are parsed as local dates to avoid timezone issues when displaying
 * dates that represent "a day" rather than "a moment in time"
 */
export function parseDate(dateString: string): Date {
  // Extract just the date portion (YYYY-MM-DD) from any format
  // This handles: "2025-12-03", "2025-12-03T00:00:00.000Z", "2025-12-03T05:30:00Z", etc.
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a date string for display, handling timezone issues correctly
 */
export function formatDateDisplay(dateString: string, options?: Intl.DateTimeFormatOptions): string {
  const date = parseDate(dateString);
  return date.toLocaleDateString('en-US', options || {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * A later-dated (scheduled) deactivation takes effect at 11:59 PM Central on the
 * deactivation date, so the service runs THROUGH that whole local day (RUT-1303).
 *
 * Returns the short "through {date}" label shown on the service, plus a precise
 * tooltip string describing the exact date AND time the service deactivates.
 *
 * @example
 * deactivationDisplay('2026-06-25')
 * // {
 * //   label: 'through Jun 25, 2026',
 * //   tooltip: 'Deactivates Wednesday, June 25, 2026 at 11:59 PM CT',
 * // }
 */
export function deactivationDisplay(deactivationDate: string): { label: string; tooltip: string } {
  const date = parseDate(deactivationDate);
  const shortDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const longDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return {
    label: `through ${shortDate}`,
    tooltip: `Deactivates ${longDate} at 11:59 PM CT`,
  };
}

/**
 * A stop that already happened (RUT-3885). Past tense, sentence case.
 *
 * @example
 * deactivatedDisplay('2026-08-28')
 * // { label: 'Deactivated on Aug 28, 2026', tooltip: 'Stopped Friday, August 28, 2026. No visits go out.' }
 */
export function deactivatedDisplay(deactivationDate: string): { label: string; tooltip: string } {
  const date = parseDate(deactivationDate);
  const shortDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const longDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return { label: `Deactivated on ${shortDate}`, tooltip: `Stopped ${longDate}. No visits go out.` };
}

/**
 * Deferred start (RUT-2910). A service can be activated now but held until a
 * future start date, at which point it goes live automatically (within its
 * season). The start takes effect at the START of the chosen local day.
 *
 * Returns the short "Requested {date}" label shown on the held service, plus a
 * precise tooltip describing when it goes live. The date is the customer's
 * requested start, which the office can change (RUT-3684); it still holds
 * dispatch and charges until then (RUT-2910). Every admin surface that shows
 * this date goes through here, so the word lives in one place.
 *
 * @example
 * startDisplay('2026-06-25')
 * // {
 * //   label: 'Requested Jun 25, 2026',
 * //   tooltip: 'Goes live Wednesday, June 25, 2026',
 * // }
 */
/** Word in front of a held service's start date (RUT-3684); lives in @ruta/shared so office emails match (RUT-4216). */
export { REQUESTED_START_LABEL };

export function startDisplay(startDate: string): { label: string; tooltip: string } {
  const date = parseDate(startDate);
  const shortDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const longDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return {
    label: `${REQUESTED_START_LABEL} ${shortDate}`,
    tooltip: `Goes live ${longDate}`,
  };
}